import { useCallback, useEffect, useState } from "react";
import {
  CheckCircle2,
  Loader2,
  PlugZap,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
  XCircle,
} from "lucide-react";

import { ApiError, api } from "../../api/client";
import Toast from "../../components/Toast";

/**
 * Settings → Payroll (PSIber) — super admin only.
 *
 * Foundation for the Paysol/PSIber payroll integration (SA / Shared
 * Services). Connection test, read-only endpoint explorer for capturing
 * real response shapes, leave type mapping, flow toggles and sync log.
 * The sync flows themselves ship separately; the toggles gate them.
 */

interface SitePayroll {
  site_id: number;
  site_name?: string;
  company_reg_no: string | null;
  payroll_reg_no: string | null;
}

interface SiteOption {
  id: number;
  name: string;
  region_name: string;
}

interface PsiberStatus {
  configured: boolean;
  base_url: string;
  username: string | null;
  site_payrolls: SitePayroll[];
  sites: SiteOption[];
  config: {
    push_employees_enabled: boolean;
    push_leave_enabled: boolean;
    pull_balances_enabled: boolean;
    pull_payslips_enabled: boolean;
    last_balance_sync_at: string | null;
  };
}

interface MappingRow {
  leave_type_id: number;
  leave_type_name: string;
  leave_type_code: string;
  region_name: string;
  psiber_code: string | null;
  psiber_description: string | null;
}

interface LogEntry {
  id: number;
  sync_type: string;
  status: string;
  started_at: string;
  error: string | null;
  triggered_by: string | null;
}

const PROBE_ENDPOINTS = [
  { path: "api/company_list", label: "Companies" },
  { path: "api/payroll_list", label: "Payrolls" },
  { path: "api/employee_list", label: "Employees" },
  { path: "api/employee_list_details", label: "Employee details" },
  { path: "api/employee_master_info", label: "Employee master info" },
  { path: "api/applicable_leave_types", label: "Leave types" },
  { path: "api/leave_status", label: "Leave statuses" },
  { path: "api/employee_annual_leave_balance_adjustments", label: "Annual leave balances" },
  { path: "api/employee_sick_leave_balance_adjustments", label: "Sick leave balances" },
] as const;

const FLOWS = [
  { key: "push_employees_enabled", label: "Push new employees to PSIber" },
  { key: "push_leave_enabled", label: "Push approved leave to PSIber" },
  { key: "pull_balances_enabled", label: "Pull leave balances from PSIber" },
  { key: "pull_payslips_enabled", label: "Pull payslips / IRP5s from PSIber" },
] as const;

export default function PsiberTab() {
  const [status, setStatus] = useState<PsiberStatus | null>(null);
  const [loadError, setLoadError] = useState("");
  const [toast, setToast] = useState("");

  // Explorer-only: which registration numbers the read-only lookups use.
  const [companyRegNo, setCompanyRegNo] = useState("");
  const [payrollRegNo, setPayrollRegNo] = useState("");
  const [flows, setFlows] = useState<Record<string, boolean>>({});
  const [savingConfig, setSavingConfig] = useState(false);

  const [sitePayrolls, setSitePayrolls] = useState<SitePayroll[]>([]);
  const [savingPayrolls, setSavingPayrolls] = useState(false);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const [probing, setProbing] = useState<string | null>(null);
  const [probeResult, setProbeResult] = useState<{ path: string; json: string } | null>(null);
  const [probeError, setProbeError] = useState("");
  const [probeEmployeeId, setProbeEmployeeId] = useState("");

  const [mappings, setMappings] = useState<MappingRow[]>([]);
  const [savingMappings, setSavingMappings] = useState(false);

  const [log, setLog] = useState<LogEntry[]>([]);

  const load = useCallback(async () => {
    try {
      const s = await api.get<PsiberStatus>("/api/psiber/status");
      setStatus(s);
      setSitePayrolls(s.site_payrolls);
      setFlows({
        push_employees_enabled: s.config.push_employees_enabled,
        push_leave_enabled: s.config.push_leave_enabled,
        pull_balances_enabled: s.config.pull_balances_enabled,
        pull_payslips_enabled: s.config.pull_payslips_enabled,
      });
      const m = await api.get<{ leave_types: MappingRow[] }>("/api/psiber/mappings");
      setMappings(m.leave_types);
      const l = await api.get<{ entries: LogEntry[] }>("/api/psiber/sync-log");
      setLog(l.entries);
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : "Failed to load PSIber settings");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post<{ ok: boolean; token_expires_at: string | null }>(
        "/api/psiber/test-connection",
      );
      setTestResult({ ok: true, message: `Authenticated. Token valid until ${r.token_expires_at ? new Date(r.token_expires_at).toLocaleString() : "unknown"}.` });
    } catch (e) {
      setTestResult({ ok: false, message: e instanceof ApiError ? e.message : "Connection failed" });
    } finally {
      setTesting(false);
      void load();
    }
  };

  const probe = async (path: string) => {
    setProbing(path);
    setProbeError("");
    setProbeResult(null);
    try {
      const params = new URLSearchParams({ path });
      if (companyRegNo) params.set("companyRegNo", companyRegNo);
      if (payrollRegNo) params.set("payrollRegNo", payrollRegNo);
      if (probeEmployeeId) params.set("employeeId", probeEmployeeId);
      const r = await api.get<{ path: string; data: unknown }>(`/api/psiber/probe?${params.toString()}`);
      setProbeResult({ path: r.path, json: JSON.stringify(r.data, null, 2) });
    } catch (e) {
      setProbeError(e instanceof ApiError ? e.message : "Request failed");
    } finally {
      setProbing(null);
      void load();
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.put("/api/psiber/config", { ...flows });
      setToast("Sync switches saved");
    } catch (e) {
      setToast(e instanceof ApiError ? e.message : "Save failed");
    } finally {
      setSavingConfig(false);
    }
  };

  const savePayrolls = async () => {
    setSavingPayrolls(true);
    try {
      const r = await api.put<{ site_payrolls: SitePayroll[] }>("/api/psiber/site-payrolls", {
        site_payrolls: sitePayrolls.map((p) => ({
          site_id: p.site_id,
          company_reg_no: (p.company_reg_no ?? "").trim() || null,
          payroll_reg_no: (p.payroll_reg_no ?? "").trim() || null,
        })),
      });
      setSitePayrolls(r.site_payrolls);
      setToast("Payrolls saved");
    } catch (e) {
      setToast(e instanceof ApiError ? e.message : "Save failed");
    } finally {
      setSavingPayrolls(false);
    }
  };

  const unusedSites = (status?.sites ?? []).filter(
    (s) => !sitePayrolls.some((p) => p.site_id === s.id),
  );

  const updatePayroll = (i: number, patch: Partial<SitePayroll>) =>
    setSitePayrolls((prev) => prev.map((row, j) => (j === i ? { ...row, ...patch } : row)));

  const saveMappings = async () => {
    setSavingMappings(true);
    try {
      const rows = mappings
        .filter((m) => (m.psiber_code ?? "").trim() !== "")
        .map((m) => ({
          leave_type_id: m.leave_type_id,
          psiber_code: (m.psiber_code ?? "").trim(),
          psiber_description: m.psiber_description || null,
        }));
      await api.put("/api/psiber/mappings", { mappings: rows });
      setToast(`Saved ${rows.length} leave type mapping${rows.length === 1 ? "" : "s"}`);
    } catch (e) {
      setToast(e instanceof ApiError ? e.message : "Save failed");
    } finally {
      setSavingMappings(false);
    }
  };

  if (loadError) {
    return <p className="text-sm text-red-600">{loadError}</p>;
  }
  if (!status) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 size={14} className="animate-spin" aria-hidden /> Loading…
      </p>
    );
  }

  return (
    <div className="max-w-4xl space-y-5">
      {toast && <Toast message={toast} onDismiss={() => setToast("")} />}

      {/* Connection */}
      <section className="card p-4">
        <h2 className="text-sm font-semibold text-gray-900">PSIber connection</h2>
        <p className="mt-0.5 text-xs text-gray-500">
          Paysol payroll (South Africa / Shared Services). Credentials live in Azure Key
          Vault — they are never stored in the app or shown here.
        </p>
        {status.configured ? (
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">
              <CheckCircle2 size={13} aria-hidden /> Credentials configured
            </span>
            <span className="text-xs text-gray-500">
              {status.username} · {status.base_url}
            </span>
            <button
              type="button"
              onClick={() => void testConnection()}
              disabled={testing}
              className="btn-primary inline-flex items-center gap-1.5 text-xs"
            >
              {testing ? <Loader2 size={13} className="animate-spin" aria-hidden /> : <PlugZap size={13} aria-hidden />}
              Test connection
            </button>
          </div>
        ) : (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <p className="font-medium">Not configured yet.</p>
            <p className="mt-1">
              Add three secrets to the <span className="font-mono">pme-hr-kv</span> Key Vault —{" "}
              <span className="font-mono">psiber-username</span>,{" "}
              <span className="font-mono">psiber-password</span> and{" "}
              <span className="font-mono">psiber-instance</span> — then restart the app.
              Nothing calls PSIber until they exist.
            </p>
          </div>
        )}
        {testResult && (
          <p
            className={`mt-2 inline-flex items-center gap-1.5 text-xs ${testResult.ok ? "text-green-700" : "text-red-600"}`}
          >
            {testResult.ok ? <CheckCircle2 size={13} aria-hidden /> : <XCircle size={13} aria-hidden />}
            {testResult.message}
          </p>
        )}
      </section>

      {/* Payrolls by site */}
      <section className="card space-y-3 p-4">
        <h2 className="text-sm font-semibold text-gray-900">Payrolls by site</h2>
        <p className="text-xs text-gray-500">
          Each company is a separate payroll in PSIber. Employees sync to the payroll of
          their site; a site not listed here never syncs to PSIber. Get the numbers from
          the Companies and Payrolls lookups below.
        </p>
        {sitePayrolls.length === 0 ? (
          <p className="text-xs text-gray-400">No sites linked to PSIber yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs text-gray-500">
                  <th className="py-1.5 pr-3 font-medium">Site</th>
                  <th className="py-1.5 pr-3 font-medium">Company reg no</th>
                  <th className="py-1.5 pr-3 font-medium">Payroll reg no</th>
                  <th className="py-1.5 font-medium" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {sitePayrolls.map((p, i) => {
                  const site = status.sites.find((s) => s.id === p.site_id);
                  return (
                    <tr key={p.site_id} className="border-b border-gray-100">
                      <td className="py-1.5 pr-3 text-gray-900">
                        {site?.name ?? p.site_name ?? `Site ${p.site_id}`}
                        {site && (
                          <span className="block text-[11px] text-gray-400">{site.region_name}</span>
                        )}
                      </td>
                      <td className="py-1.5 pr-3">
                        <input
                          value={p.company_reg_no ?? ""}
                          onChange={(e) => updatePayroll(i, { company_reg_no: e.target.value })}
                          className="w-40 rounded-md border border-gray-300 px-2 py-1 text-xs"
                        />
                      </td>
                      <td className="py-1.5 pr-3">
                        <input
                          value={p.payroll_reg_no ?? ""}
                          onChange={(e) => updatePayroll(i, { payroll_reg_no: e.target.value })}
                          className="w-40 rounded-md border border-gray-300 px-2 py-1 text-xs"
                        />
                      </td>
                      <td className="py-1.5 whitespace-nowrap text-right">
                        <button
                          type="button"
                          onClick={() => {
                            setCompanyRegNo(p.company_reg_no ?? "");
                            setPayrollRegNo(p.payroll_reg_no ?? "");
                          }}
                          className="mr-2 text-xs text-pme-blue hover:underline"
                          title="Use these numbers in the lookups below"
                        >
                          Use in lookups
                        </button>
                        <button
                          type="button"
                          onClick={() => setSitePayrolls((prev) => prev.filter((_, j) => j !== i))}
                          className="inline-flex items-center text-gray-400 hover:text-red-600"
                          aria-label={`Remove ${site?.name ?? "site"}`}
                        >
                          <Trash2 size={13} aria-hidden />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {unusedSites.length > 0 && (
            <select
              value=""
              onChange={(e) => {
                const id = Number(e.target.value);
                if (!id) return;
                setSitePayrolls((prev) => [
                  ...prev,
                  { site_id: id, company_reg_no: null, payroll_reg_no: null },
                ]);
              }}
              className="rounded-md border border-gray-300 px-2 py-1.5 text-xs text-gray-700"
              aria-label="Add a site"
            >
              <option value="">Add a site…</option>
              {unusedSites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.region_name})
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => void savePayrolls()}
            disabled={savingPayrolls}
            className="btn-primary inline-flex items-center gap-1.5 text-xs"
          >
            {savingPayrolls ? <Loader2 size={13} className="animate-spin" aria-hidden /> : <Save size={13} aria-hidden />}
            Save payrolls
          </button>
          {unusedSites.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
              <Plus size={11} aria-hidden /> pick a site to add a row
            </span>
          )}
        </div>
      </section>

      {/* Flow toggles */}
      <section className="card space-y-3 p-4">
        <h2 className="text-sm font-semibold text-gray-900">Sync switches</h2>
        <div className="space-y-1.5">
          {FLOWS.map((f) => (
            <label key={f.key} className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={!!flows[f.key]}
                onChange={(e) => setFlows((prev) => ({ ...prev, [f.key]: e.target.checked }))}
              />
              {f.label}
            </label>
          ))}
          <p className="text-xs text-gray-400">
            The sync flows are being built one at a time (leave balances first). A flow only
            runs when its switch is on AND the code for it has shipped.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void saveConfig()}
          disabled={savingConfig}
          className="btn-primary inline-flex items-center gap-1.5 text-xs"
        >
          {savingConfig ? <Loader2 size={13} className="animate-spin" aria-hidden /> : <Save size={13} aria-hidden />}
          Save switches
        </button>
      </section>

      {/* Endpoint explorer */}
      <section className="card space-y-3 p-4">
        <h2 className="text-sm font-semibold text-gray-900">Endpoint explorer (read-only)</h2>
        <p className="text-xs text-gray-500">
          Calls a fixed list of read-only PSIber lookups and shows the raw response — use it
          to find the registration numbers and confirm what the API returns. Start with
          Companies, then Payrolls, then Employees. Every call is logged below.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="block text-xs font-medium text-gray-600">
            Company reg no
            <input
              value={companyRegNo}
              onChange={(e) => setCompanyRegNo(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm"
              placeholder="For Payrolls"
            />
          </label>
          <label className="block text-xs font-medium text-gray-600">
            Payroll reg no
            <input
              value={payrollRegNo}
              onChange={(e) => setPayrollRegNo(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm"
              placeholder="For Employees"
            />
          </label>
          <label className="block text-xs font-medium text-gray-600">
            Employee ID
            <input
              value={probeEmployeeId}
              onChange={(e) => setProbeEmployeeId(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm"
              placeholder="For master info"
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PROBE_ENDPOINTS.map((p) => (
            <button
              key={p.path}
              type="button"
              onClick={() => void probe(p.path)}
              disabled={probing !== null || !status.configured}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {probing === p.path ? (
                <Loader2 size={12} className="animate-spin" aria-hidden />
              ) : (
                <Search size={12} aria-hidden />
              )}
              {p.label}
            </button>
          ))}
        </div>
        {probeError && <p className="text-xs text-red-600">{probeError}</p>}
        {probeResult && (
          <div>
            <p className="mb-1 text-xs font-medium text-gray-600">
              <span className="font-mono">{probeResult.path}</span>
            </p>
            <pre className="max-h-80 overflow-auto rounded-md border border-gray-200 bg-gray-50 p-3 text-[11px] leading-snug text-gray-800">
              {probeResult.json}
            </pre>
          </div>
        )}
      </section>

      {/* Leave type mapping */}
      <section className="card space-y-3 p-4">
        <h2 className="text-sm font-semibold text-gray-900">Leave type mapping</h2>
        <p className="text-xs text-gray-500">
          Map PME People leave types to PSIber leave codes (pull the codes with the
          "Leave types" lookup above). Only mapped types will sync. SA and Shared
          Services types are the ones that matter here.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs text-gray-500">
                <th className="py-1.5 pr-3 font-medium">Leave type</th>
                <th className="py-1.5 pr-3 font-medium">Region</th>
                <th className="py-1.5 pr-3 font-medium">PSIber code</th>
                <th className="py-1.5 font-medium">PSIber description</th>
              </tr>
            </thead>
            <tbody>
              {mappings.map((m, i) => (
                <tr key={m.leave_type_id} className="border-b border-gray-100">
                  <td className="py-1.5 pr-3 text-gray-900">{m.leave_type_name}</td>
                  <td className="py-1.5 pr-3 text-xs text-gray-500">{m.region_name}</td>
                  <td className="py-1.5 pr-3">
                    <input
                      value={m.psiber_code ?? ""}
                      onChange={(e) =>
                        setMappings((prev) =>
                          prev.map((row, j) => (j === i ? { ...row, psiber_code: e.target.value } : row)),
                        )
                      }
                      className="w-28 rounded-md border border-gray-300 px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="py-1.5">
                    <input
                      value={m.psiber_description ?? ""}
                      onChange={(e) =>
                        setMappings((prev) =>
                          prev.map((row, j) => (j === i ? { ...row, psiber_description: e.target.value } : row)),
                        )
                      }
                      className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          onClick={() => void saveMappings()}
          disabled={savingMappings}
          className="btn-primary inline-flex items-center gap-1.5 text-xs"
        >
          {savingMappings ? <Loader2 size={13} className="animate-spin" aria-hidden /> : <Save size={13} aria-hidden />}
          Save mappings
        </button>
      </section>

      {/* Activity log */}
      <section className="card space-y-2 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Activity log</h2>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-1 text-xs text-pme-blue hover:underline"
          >
            <RefreshCw size={12} aria-hidden /> Refresh
          </button>
        </div>
        {log.length === 0 ? (
          <p className="text-xs text-gray-400">No PSIber activity yet.</p>
        ) : (
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-gray-500">
                <th className="py-1.5 pr-3 font-medium">When</th>
                <th className="py-1.5 pr-3 font-medium">Action</th>
                <th className="py-1.5 pr-3 font-medium">Status</th>
                <th className="py-1.5 pr-3 font-medium">By</th>
                <th className="py-1.5 font-medium">Error</th>
              </tr>
            </thead>
            <tbody>
              {log.map((e) => (
                <tr key={e.id} className="border-b border-gray-100">
                  <td className="py-1.5 pr-3 whitespace-nowrap text-gray-500">
                    {new Date(e.started_at).toLocaleString()}
                  </td>
                  <td className="py-1.5 pr-3 font-mono text-[11px] text-gray-700">{e.sync_type}</td>
                  <td className="py-1.5 pr-3">
                    <span
                      className={
                        e.status === "success"
                          ? "font-medium text-green-700"
                          : "font-medium text-red-600"
                      }
                    >
                      {e.status}
                    </span>
                  </td>
                  <td className="py-1.5 pr-3 text-gray-500">{e.triggered_by ?? "—"}</td>
                  <td className="py-1.5 text-red-600">{e.error ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
